import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FormInstance, notification } from "antd";
import {
  dummyAddress,
  defaultSelectedAddress,
  Address,
} from "../types/LocationTypes";
import { InsertParameter, RegistrationEnums } from "../types/ApplicationTypes";
import { useTripsCtx } from "../context/TripDetailsContext";
import { usePersonSummaryCtx } from "../context/PersonSummaryContext";
import { PersonInitialValues } from "../types/PersonTypes";
import {
  updateAdditionalDetailsForPerson,
  uploadCCPDetailsImage,
} from "../services/RegistrationService";
import { useServiceUserCreationCtx } from "../context/ServiceUserCreationContext";
import { LookupListItems } from "../types/SubscriptionTypes";
import {
  updateUserEmailAddress,
  getUserEmailAddressStatus,
} from "../services/ProfilingService";
import {
  calculateAge,
  getInitialAddress,
  getServiceUserDetails,
  isValidPhone,
  validateAddressObject,
} from "../helpers/personHelpers";
import { IServiceUserInfo } from "../types/ServiceUserInfo";

export function useProfileActions(
  form: FormInstance,
  personId: number,
  initialValues: PersonInitialValues,
  setIsInvalidEmailModalVisible: Function,
  navigateOnSubmitForm: Function
) {
  const [shouldDisableBtn, setShouldDisableBtn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { pathname } = useLocation();

  const {
    caregiverSummaryInfo,
    setCaregiverSummaryInfo,
    isWandaConcierge,
  } = usePersonSummaryCtx();

  const { wandaOrgId, serviceDataCtx } = useTripsCtx();
  const [serviceData, setServiceData] = serviceDataCtx;

  const { relationshipTypesCtx } = useServiceUserCreationCtx();
  const [relationshipTypes] = relationshipTypesCtx;

  useEffect(() => {
    validateForm();
  }, [initialValues]);

  const profileInfo = useMemo(() => {
    const selectedServiceUser = getServiceUserDetails(personId, serviceData);
    const isCaregiverProfile = caregiverSummaryInfo?.personId === personId;
    const isEditProfile = pathname.split("/").includes("EditProfile");
    const isEmailDisabled =
      (!isWandaConcierge && isCaregiverProfile) ||
      (isWandaConcierge && isCaregiverProfile && !isEditProfile);

    return {
      isCaregiverProfile,
      isActiveServiceUser: selectedServiceUser?.personId === personId,
      isEmailDisabled,
      selectedServiceUser,
      wandaEmailAddressAttribute: isCaregiverProfile
        ? caregiverSummaryInfo.wandaEmailAddressAttribute
        : selectedServiceUser?.personAttributes.personContact
            .wandaEmailAddressAttribute,
      initialImageKey: isCaregiverProfile
        ? caregiverSummaryInfo?.imageKey
        : selectedServiceUser?.imageKey,
    };
  }, [serviceData]);

  function getFormFieldValues() {
    return form.getFieldsValue([
      "firstName",
      "lastName",
      "phone",
      "email",
      "relationship",
      "dateOfBirth",
      "homeAddress",
      "mailingAddress",
      "photo",
      "photoFile",
      "wandaAccommodations",
    ]);
  }

  function validateForm() {
    const {
      phone,
      relationship,
      homeAddress,
      mailingAddress,
    } = getFormFieldValues();

    const { isCaregiverProfile, isActiveServiceUser } = profileInfo;

    function isDummyAddress(address: Address) {
      return !getInitialAddress(address).address1;
    }

    const isValidBasicInfo =
      isValidPhone(phone) && (isCaregiverProfile || relationship);
    const isValidHomeAddress =
      (!isActiveServiceUser &&
        isDummyAddress(caregiverSummaryInfo.homeAddress)) ||
      validateAddressObject(homeAddress || defaultSelectedAddress);
    const isValidMailingAddress =
      (!isActiveServiceUser &&
        isDummyAddress(caregiverSummaryInfo.mailingAddress)) ||
      validateAddressObject(mailingAddress || defaultSelectedAddress);

    const isInvalidFieldExists = !(
      isValidBasicInfo &&
      isValidHomeAddress &&
      isValidMailingAddress
    );

    isInvalidFieldExists
      ? setShouldDisableBtn(true)
      : setShouldDisableBtn(false);
  }

  async function handleSubmitForm() {
    try {
      setIsLoading(true);
      const {
        firstName,
        lastName,
        email,
        phone,
        relationship,
        dateOfBirth,
        homeAddress,
        mailingAddress,
        photo,
        photoFile,
        wandaAccommodations,
      } = getFormFieldValues();

      const {
        wandaEmailAddressAttribute,
        isActiveServiceUser,
        initialImageKey,
        isEmailDisabled,
        isCaregiverProfile,
      } = profileInfo;
      const {
        fullAddress: homeFullAddress,
        ...homeRest
      } = homeAddress?.address1 ? homeAddress : dummyAddress;
      const {
        fullAddress: mailingFullAddress,
        ...mailingRest
      } = mailingAddress?.address1 ? mailingAddress : dummyAddress;
      const wandaRelation = relationshipTypes?.find(
        (relation: LookupListItems) => relation.value === relationship
      )?.label;

      if (
        isWandaConcierge &&
        isCaregiverProfile &&
        !isEmailDisabled &&
        caregiverSummaryInfo.wandaEmail !== email
      ) {
        const { subjectId } = await getUserEmailAddressStatus(
          caregiverSummaryInfo.wandaEmail
        );

        await updateUserEmailAddress({
          subjectId,
          email,
          username: email,
        });
      }

      const updatedProfileData = {
        dateOfBirth:
          dateOfBirth?.format("YYYY-MM-DD") || RegistrationEnums.DateOfBirth,
        firstName,
        lastName,
        personId,
        keyTypeId: RegistrationEnums.KeyTypeId,
        isActive: true,
        personTypeId: RegistrationEnums.PersonTypeId,
        organizationId: wandaOrgId.benefitGroupId,
        zipCode: homeRest.zip,
        personAttributes: {
          personInfo: {
            primaryLanguage: "English",
          },
          homeAddress: homeRest,
          mailingAddress: mailingRest,
          personContact: {
            WandaEmail: email,
            wandaEmailAddressAttribute,
            primaryPhone: phone,
            wandaRelation,
          },
        },
        wandaAccommodations: isActiveServiceUser
          ? wandaAccommodations
          : undefined,
      };

      let uploadedImageKey = {} as { key: string };
      if (photoFile !== undefined) {
        uploadedImageKey = await uploadCCPDetailsImage(photoFile, personId);
      }
      const serviceUserStatusData = {
        imageKey: uploadedImageKey.key || initialImageKey,
        dobUpdated: dateOfBirth ? true : false,
        insertParameter: InsertParameter.updateRecord,
        updatePersonPersonRel: false,
        caregiverPersonId: personId,
      };

      const personCreationData = {
        createPersonData: updatedProfileData,
        createPersonStatusInfo: serviceUserStatusData,
      };

      await updateAdditionalDetailsForPerson(personCreationData);

      if (isCaregiverProfile) {
        setCaregiverSummaryInfo({
          ...caregiverSummaryInfo,
          wandaEmail: email,
          primaryPhone: phone,
          imageKey: uploadedImageKey.key || initialImageKey,
          imageUrl: photo,
          dateOfBirth: updatedProfileData.dateOfBirth,
          homeAddress: homeRest,
          mailingAddress: mailingRest,
        });
      }

      let updatedServiceUsersList;
      if (isActiveServiceUser && serviceData.length) {
        updatedServiceUsersList = serviceData?.map(
          (serviceUser: IServiceUserInfo) => {
            if (serviceUser.personId === personId) {
              return {
                ...serviceUser,
                dateOfBirth: updatedProfileData.dateOfBirth,
                profileImageUrl: photo,
                imageKey: uploadedImageKey.key || initialImageKey,
                personAttributes: {
                  homeAddress: homeRest,
                  mailingAddress: mailingRest,
                  personContact: {
                    primaryEmailAddress:
                      serviceUser.personAttributes.personContact
                        .primaryEmailAddress,
                    primaryPhone: phone,
                    wandaEmail: email,
                    wandaRelation,
                  },
                },
                wandaAccommodations,
                age: calculateAge(updatedProfileData.dateOfBirth),
              };
            } else {
              return serviceUser;
            }
          }
        );
        setServiceData(updatedServiceUsersList);
      }

      navigateOnSubmitForm(updatedServiceUsersList);
    } catch (error: any) {
      if (error?.message === "InvalidEmail") {
        setIsInvalidEmailModalVisible(true);
      } else {
        notification.error({
          message: "Failed to Update Profile",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  return {
    shouldDisableBtn,
    validateForm,
    profileInfo,
    handleSubmitForm,
    isLoading,
  };
}
